"use client"

import { useState, useEffect, useRef, type MouseEvent } from "react"
import { usePathname } from "next/navigation"
import type { components } from "umbraco"
import { getMediaUrl, getLinkUrl } from "umbraco"
import { getUmbracoMediaUrl } from "utils/getUmbracoMediaUrl"
import { getNonUmbracoUrl } from "utils"
import { scrollInstantly } from "../../common/scrollInstantly"
import { isScrollIntentKey } from "../../common/scrollIntent"
import SearchIcon from "../../icons/SearchIcon"
import HamburgerIcon from "../../icons/HamburgerIcon"
import { useContactModal } from "../ContactModal/context"
import {
  HeaderWrapper,
  HeaderInner,
  LinkHeaderBar,
  LinkDivider,
  ServiceTabLink,
  MainHeaderBar,
  LogoLink,
  LogoImageWrapper,
  LogoImage,
  IconGroup,
  IconButton,
  CtaButton,
  CtaButtonAsButton,
  ScrollingPillWrapper,
  ScrollingPillContainer,
  PillSeparator,
  PillCtaButton,
  PillCtaButtonAsButton,
  PillLogoLink,
  PillLogoIcon,
  PillHamburgerButton,
} from "./styles"

type HeaderProperties = components["schemas"]["HeaderPropertiesModel"]

export interface MenuItemData {
  title: string
  link: string | null
  /** EGDE-351: link target ("_blank" opens in a new tab) from the CMS picker. */
  target?: string
  children?: MenuItemData[]
}

export interface HeaderData {
  properties: HeaderProperties | null
  menuItems: MenuItemData[]
}

interface HeaderProps {
  data: HeaderData
  locale: string
  isHomepage?: boolean
  onOpenMenu?: () => void
  onOpenSearch?: () => void
}

/**
 * EGDE-484: jump to the very top instantly and hold it there briefly —
 * late browser scroll restoration (after a mid-page reload) and Next's
 * own post-navigation scroll handling both fire moments later and would
 * otherwise drag the page back down. Real user input releases the hold.
 *
 * EGDE-719: every scroll here suppresses animation. `html` now carries
 * `scroll-behavior: smooth`, and the two-argument `scrollTo(x, y)` form takes
 * its behaviour from the CSS — so the hold would have re-launched a ~700ms
 * animation on every frame it found the page off zero and never arrived inside
 * its own one-second window. This is a snap, not a journey.
 */
function pinScrollTop() {
  scrollInstantly(() => window.scrollTo(0, 0))
  const stopAt = Date.now() + 1000
  let released = false
  // EGDE-719 (r3 follow-up): the same rule the anchor landing needed. A tap or
  // a keystroke is not a scroll, and releasing the pin on one lets whatever
  // late scroll restoration is still arriving drag the page back down inside
  // the hold window. Declared before its listener so the pair reads in the
  // order it initialises.
  const release = () => {
    released = true
    window.removeEventListener("wheel", release)
    window.removeEventListener("touchmove", release)
    window.removeEventListener("keydown", onKey)
  }
  const onKey = (event: KeyboardEvent) => {
    if (isScrollIntentKey(event)) release()
  }
  window.addEventListener("wheel", release, { passive: true })
  window.addEventListener("touchmove", release, { passive: true })
  window.addEventListener("keydown", onKey)
  const hold = () => {
    if (released || Date.now() > stopAt) {
      release()
      return
    }
    if (window.scrollY !== 0) scrollInstantly(() => window.scrollTo(0, 0))
    requestAnimationFrame(hold)
  }
  requestAnimationFrame(hold)
}

/**
 * Normalize a pathname for comparison:
 * strip locale prefix (/he/, /en/) and trailing slash
 */
function normalizePath(path: string): string {
  let normalized = path.replace(/^\/(he|en|ar|ru)(\/|$)/, "/")
  if (normalized !== "/" && normalized.endsWith("/")) {
    normalized = normalized.slice(0, -1)
  }
  return normalized
}

export default function Header({
  data,
  locale,
  isHomepage = false,
  onOpenMenu,
  onOpenSearch,
}: HeaderProps) {
  const properties = data.properties
  const pathname = usePathname()
  const { open: openContactModal, isEnabled: isContactModalEnabled } =
    useContactModal()

  const [isScrolled, setIsScrolled] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      const next = window.scrollY > 10
      setIsScrolled((prev) => (prev === next ? prev : next))
    }
    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  if (!properties) return null

  const logoUrl = getMediaUrl(properties.logo)
  const logoSrc = logoUrl ? getUmbracoMediaUrl(logoUrl) : null

  const logoIconUrl = getMediaUrl(properties.logoIcon)
  const logoIconSrc = logoIconUrl ? getUmbracoMediaUrl(logoIconUrl) : null

  // EGDE-484: a logo click must land the visitor at the TOP of the homepage.
  // Neither half of that can be left to the router:
  //  - already on the homepage: a same-route push can land on an arbitrary
  //    restored scroll position, so navigation is prevented entirely;
  //  - coming from another page: Next's scroll-to-top heuristic skips
  //    fixed/sticky leading elements, and the homepage's pinned hero defeats
  //    it — the arrival landed mid-page. The Header survives navigation, so
  //    the click arms a flag and the pathname-change effect below pins the
  //    scroll once the homepage is in.
  // pinScrollTop HOLDS the top for a moment: after a reload mid-page,
  // Chromium keeps re-applying scroll restoration while content settles and
  // can yank the page back down. Any real user input releases the hold.
  const scrollHomeTopRef = useRef(false)
  const handleLogoClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (normalizePath(pathname ?? "") === "/") {
      e.preventDefault()
      pinScrollTop()
    } else {
      scrollHomeTopRef.current = true
    }
  }
  useEffect(() => {
    if (!scrollHomeTopRef.current) return
    if (normalizePath(pathname ?? "") !== "/") return
    scrollHomeTopRef.current = false
    pinScrollTop()
  }, [pathname])

  // EGDE-285: the Contact CTA only opens the contact pop-up — it carries no
  // link. It is now a plain title in the CMS, but tolerate the legacy
  // MultiUrlPicker shape too so this works regardless of type-regen timing.
  const contactCtaRaw = properties.contactCta as
    | string
    | { title?: string }[]
    | null
    | undefined
  const contactCtaTitle =
    typeof contactCtaRaw === "string"
      ? contactCtaRaw
      : (contactCtaRaw?.[0]?.title ?? "")
  const contactCtaUrl = getLinkUrl(properties.contactCta)

  const serviceTabs = properties.serviceTabs ?? []

  return (
    <>
      <HeaderWrapper
        $isHomepage={isHomepage}
        $isVisible={!isScrolled}
        data-component="header"
      >
        <HeaderInner>
          <LinkHeaderBar $isHomepage={isHomepage}>
            {serviceTabs.map((tab, i) => {
              const tabHref = getNonUmbracoUrl(
                tab.url ?? tab.route?.path ?? undefined,
              )
              const normalizedTab = normalizePath(tabHref)
              const normalizedPathname = normalizePath(pathname ?? "")
              // EGDE-495: a tab that targets THIS site's homepage is the
              // "you are here" tab — while the visitor is anywhere under the
              // eggedplus domain it always reads as selected. The other tabs
              // lead to external sites and only underline on hover.
              const isSiteHomeTab =
                normalizedTab === "/" && tabHref.startsWith("/")
              const isActive =
                isSiteHomeTab ||
                (!!normalizedTab &&
                  normalizedTab !== "/" &&
                  normalizedPathname.startsWith(normalizedTab))
              // EGDE-567: honour the CMS picker's "open in new window" —
              // the target never reached the anchor.
              const tabTarget = tab.target ?? undefined
              return (
                <ServiceTabLink
                  key={i}
                  href={tabHref}
                  target={tabTarget}
                  rel={
                    tabTarget === "_blank" ? "noopener noreferrer" : undefined
                  }
                  $isHomepage={isHomepage}
                  $isActive={isActive}
                >
                  {tab.title}
                </ServiceTabLink>
              )
            })}
            {/* EGDE-669: the desktop language toggle (EGDE-342) is gone — English
                was discarded, so the bar carries only the service tabs. */}
          </LinkHeaderBar>
        </HeaderInner>

        <LinkDivider $isHomepage={isHomepage} />

        <HeaderInner>
          <MainHeaderBar>
            {/* RTL flex-row reversal: Icons first, Logo center, CTA last in JSX */}
            <IconGroup>
              <IconButton
                $isHomepage={isHomepage}
                onClick={onOpenMenu}
                aria-label="Open menu"
              >
                <HamburgerIcon />
              </IconButton>
              <IconButton
                $isHomepage={isHomepage}
                onClick={onOpenSearch}
                aria-label="Search"
              >
                <SearchIcon />
              </IconButton>
            </IconGroup>

            {logoSrc && (
              <LogoLink href="/" onClick={handleLogoClick}>
                <LogoImageWrapper>
                  <LogoImage
                    src={logoSrc}
                    alt="Egged Plus"
                    width={162}
                    height={56}
                    $isHomepage={isHomepage}
                  />
                </LogoImageWrapper>
              </LogoLink>
            )}

            {isContactModalEnabled ? (
              <CtaButtonAsButton
                type="button"
                onClick={openContactModal}
                aria-haspopup="dialog"
                $isHomepage={isHomepage}
              >
                {contactCtaTitle}
              </CtaButtonAsButton>
            ) : (
              contactCtaUrl && (
                <CtaButton
                  href={getNonUmbracoUrl(contactCtaUrl)}
                  $isHomepage={isHomepage}
                >
                  {contactCtaTitle}
                </CtaButton>
              )
            )}
          </MainHeaderBar>
        </HeaderInner>
      </HeaderWrapper>

      <ScrollingPillWrapper $isVisible={isScrolled}>
        <ScrollingPillContainer>
          <PillHamburgerButton onClick={onOpenMenu} aria-label="Open menu">
            <HamburgerIcon />
          </PillHamburgerButton>

          <PillSeparator />

          {logoIconSrc && (
            <PillLogoLink href="/" onClick={handleLogoClick}>
              <PillLogoIcon src={logoIconSrc} alt="Egged Plus" />
            </PillLogoLink>
          )}

          <PillSeparator />

          {isContactModalEnabled ? (
            <PillCtaButtonAsButton
              type="button"
              onClick={openContactModal}
              aria-haspopup="dialog"
            >
              {contactCtaTitle}
            </PillCtaButtonAsButton>
          ) : (
            contactCtaUrl && (
              <PillCtaButton href={getNonUmbracoUrl(contactCtaUrl)}>
                {contactCtaTitle}
              </PillCtaButton>
            )
          )}
        </ScrollingPillContainer>
      </ScrollingPillWrapper>
    </>
  )
}
